import React, { useState } from 'react';
import {Container, LoginButton} from "../../styles/Styles";
import AdminNav from "./AdminNav";
import {useMutation, useQuery} from "@apollo/client";
import AppModal from "../common/AppModal";
import AddTourItemForm from "../form/AddTourItemForm";
import AddTourForm from "../form/AddTourForm";
import {GET_TOURS_LIST, REMOVE_BOOKING_FROM_TOUR, DELETE_TOUR, UPDATE_TOUR_COORDINATORS, UPDATE_TOUR_LANGUAGE} from "../gql/tours-gql";
import moment from "moment/moment";
import TourList from "../common/TourList";
import {GET_GUIDES_LIST} from "../gql/guides-gql";
import {GET_COORDINATORS_LIST} from "../gql/coordinators-gql";
import Loader from "../common/Loader";
import {UPDATE_TOUR_GUIDES} from "../gql/tours-gql";
import {toast} from "react-toastify";
import { useAuth } from '../../context/AuthContext';
import { Roles } from '../common/roles';
import { Navigate } from 'react-router-dom';
import styled from "styled-components";
import TourCalendar from '../common/TourCalendar';
import { FaTrash } from 'react-icons/fa';
import { Languages } from '../common/languages';

const TourCard = styled.div`
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 1.5rem;
    margin-bottom: 1rem;
`;

const TourHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #eee;
`;

const TourTitle = styled.h3`
    margin: 0;
    color: #333;
    font-size: 1.2rem;
`;

const TourDetails = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
`;

const DetailLabel = styled.span`
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 0.25rem;
`;

const DetailValue = styled.span`
    font-weight: 500;
    color: #333;
`;

const BookingItem = styled.div`
    background: #f8f9fa;
    border-radius: 4px;
    padding: 1rem;
    margin-bottom: 0.5rem;
    position: relative;
`;

const RemoveButton = styled.button`
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;

    &:hover {
        color: #c82333;
    }
`;

const DeleteTourButton = styled.button`
    background-color: #dc3545;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 0.5rem 1rem;
    cursor: pointer;
    margin-top: 1rem;
    font-size: 0.95rem;

    &:hover {
        background-color: #c82333;
    }
`;

const AdminDashboardTours = () => {
    const { user, isAuthenticated } = useAuth();
    const [showModal, setShowModal] = useState(false);
    const [showModalTour, setShowModalTour] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const toursQueryVars = {
        month: currentMonth.getMonth() + 1,
        year: currentMonth.getFullYear()
    };

    const {data: toursList, loading } = useQuery(GET_TOURS_LIST, {
        variables: toursQueryVars
    });
    const tours = toursList?.getToursList || [];

    const {data: guidesList, loading: guideLoading } = useQuery(GET_GUIDES_LIST);
    const guides = guidesList?.getGuidesList || [];

    const {data: coordinatorsList, loading: coordinatorsLoading } = useQuery(GET_COORDINATORS_LIST);
    const coordinators = coordinatorsList?.getCoordinatorsList || [];

    const [updateTourGuidesMutation] = useMutation(UPDATE_TOUR_GUIDES, {
        onCompleted: () => {
            toast.success("Updated !");
        },
        refetchQueries: [{ query: GET_TOURS_LIST, variables: toursQueryVars }]
    });

    const [removeBookingFromTour] = useMutation(REMOVE_BOOKING_FROM_TOUR, {
        onCompleted: () => {
            toast.success("Booking removed from tour successfully!");
        },
        onError: (error) => {
            toast.error("Failed to remove booking: " + error.message);
        },
        refetchQueries: [{ query: GET_TOURS_LIST, variables: toursQueryVars }]
    });

    const [updateTourCoordinatorsMutation] = useMutation(UPDATE_TOUR_COORDINATORS, {
        onCompleted: () => {
            toast.success("Coordinator updated!");
        },
        onError: (error) => {
            toast.error("Failed to update coordinator: " + error.message);
        },
        refetchQueries: [{ query: GET_TOURS_LIST, variables: toursQueryVars }]
    });

    const [updateTourLanguageMutation] = useMutation(UPDATE_TOUR_LANGUAGE, {
        onCompleted: () => {
            toast.success("Tour language updated!");
        },
        onError: (error) => {
            toast.error("Failed to update tour language: " + error.message);
        },
        refetchQueries: [{ query: GET_TOURS_LIST, variables: toursQueryVars }]
    });

    const [deleteTourMutation] = useMutation(DELETE_TOUR, {
        onCompleted: () => {
            toast.success("Tour deleted successfully!");
        },
        onError: (error) => {
            toast.error("Failed to delete tour: " + error.message);
        },
        refetchQueries: [{ query: GET_TOURS_LIST, variables: toursQueryVars }]
    });

    if (!isAuthenticated || user?.role[0] !== Roles.ADMIN) {
        return <Navigate to="/" />;
    }

    const addTourLabelHandler = () => {
        setShowModal(true);
    }

    const addTourHandler = () => {
        setShowModalTour(true);
    }

    const handleMonthChange = (date: Date) => {
        setCurrentMonth(date);
    };

    const updateTourGuidesHandler = async (tourId: string, guides: string[]) => {
        await updateTourGuidesMutation({
            variables: {
                tourUpdateGuidesInput: {
                    tourId,
                    guidesIDs: guides
                }
            }
        })
    }

    const updateTourCoordinatorHandler = async (tourId: string, coordinatorId: string) => {
        await updateTourCoordinatorsMutation({
            variables: {
                tourUpdateCoordinatorsInput: {
                    tourId,
                    coordinatorsIDs: [coordinatorId]
                }
            }
        });
    }

    const updateTourLanguageHandler = async (tourId: string, language: Languages) => {
        await updateTourLanguageMutation({
            variables: {
                tourUpdateLanguageInput: {
                    tourId,
                    language
                }
            }
        });
    }

    const handleRemoveBooking = async (tourId: string, bookingId: string) => {
        if (window.confirm('Are you sure you want to remove this booking from the tour?')) {
            try {
                await removeBookingFromTour({
                    variables: {
                        tourBookingInput: {
                            tourId,
                            bookingId
                        }
                    }
                });
            } catch (error) {
                console.error("Error removing booking:", error);
            }
        }
    };

    const handleDeleteTour = async (tourId: string, closeModal?: () => void) => {
        if (!window.confirm('Are you sure you want to delete this tour?')) {
            return;
        }

        try {
            await deleteTourMutation({
                variables: {
                    tourDeleteInput: {
                        tourId
                    }
                }
            });
            closeModal?.();
        } catch (error) {
            console.error("Error deleting tour:", error);
        }
    };

    const renderTourCard = (tour: any, options?: { closeModal: () => void }) => {
        const totals = {
            adults: 0,
            youths: 0,
            children: 0,
            enfants: 0
        };

        tour?.bookings?.forEach((booking: any) => {
            if (booking.client) {
                totals.adults += booking.client.clientAdult || 0;
                totals.youths += booking.client.clientYouth || 0;
                totals.children += booking.client.clientChild || 0;
                totals.enfants += booking.client.clientEnfant || 0;
            }
        });

        return (
            <TourCard>
                <TourHeader>
                    <TourTitle>{tour.title || tour.tourTitle}</TourTitle>
                </TourHeader>
                <TourDetails>
                    <DetailItem>
                        <DetailLabel>Date</DetailLabel>
                        <DetailValue>{moment(tour.bookingDate).format("D MMM YYYY h:mm a")}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Language</DetailLabel>
                        <DetailValue>{tour.language ?? 'Not set'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Total Adults</DetailLabel>
                        <DetailValue>{totals.adults}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Total Youths</DetailLabel>
                        <DetailValue>{totals.youths}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Total Children</DetailLabel>
                        <DetailValue>{totals.children}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Total Enfants</DetailLabel>
                        <DetailValue>{totals.enfants}</DetailValue>
                    </DetailItem>
                    {tour.guide && (
                        <DetailItem>
                            <DetailLabel>Guide</DetailLabel>
                            <DetailValue>
                                {tour.guide.map((g: any) => `${g.profile.firstName} ${g.profile.lastName}`).join(', ')}
                            </DetailValue>
                        </DetailItem>
                    )}
                    {tour.coordinator && tour.coordinator.length > 0 && (
                        <DetailItem>
                            <DetailLabel>Coordinator</DetailLabel>
                            <DetailValue>
                                {tour.coordinator.map((c: any) => `${c.profile.firstName} ${c.profile.lastName}`).join(', ')}
                            </DetailValue>
                        </DetailItem>
                    )}
                </TourDetails>
                {tour.bookings && tour.bookings.length > 0 && (
                    <div>
                        <h4>Bookings</h4>
                        {tour.bookings.map((booking: any) => (
                            <BookingItem key={booking._id}>
                                <RemoveButton 
                                    onClick={() => handleRemoveBooking(tour._id, booking._id)}
                                    title="Remove booking from tour"
                                >
                                    <FaTrash />
                                </RemoveButton>
                                <div><strong>Code:</strong> {booking.bookingCode}</div>
                                <div><strong>Client:</strong> {booking.client?.clientName}</div>
                                <div><strong>Phone:</strong> {booking.client?.clientPhone}</div>
                                <div><strong>Price:</strong> €{booking.priceNet}</div>
                                <div>
                                    <strong>Customers:</strong> 
                                    Adults: {booking.client?.clientAdult || 0}, 
                                    Youths: {booking.client?.clientYouth || 0}, 
                                    Children: {booking.client?.clientChild || 0}, 
                                    Enfants: {booking.client?.clientEnfant || 0}
                                </div>
                            </BookingItem>
                        ))}
                    </div>
                )}
                <DeleteTourButton
                    type="button"
                    onClick={() => handleDeleteTour(tour._id, options?.closeModal)}
                >
                    Delete Tour
                </DeleteTourButton>
            </TourCard>
        );
    };

    if (loading || guideLoading || coordinatorsLoading) return (
        <Loader />
    );

    return (<Container>
        <AdminNav />
        <h2>Tours Management</h2>
        <LoginButton onClick={addTourLabelHandler}>Add tour labels</LoginButton>
        <LoginButton onClick={addTourHandler}>Add tour</LoginButton>

        <TourCalendar tours={tours} renderTourCard={renderTourCard} onMonthChange={handleMonthChange} currentDate={currentMonth} />

        <div className="tour-list">
            <TourList
                tours={tours}
                guides={guides}
                coordinators={coordinators}
                updateTourGuides={updateTourGuidesHandler}
                updateTourCoordinator={updateTourCoordinatorHandler}
                updateTourLanguage={updateTourLanguageHandler}
                onDeleteTour={handleDeleteTour}
            />
        </div>

        <AppModal isOpen={showModal} onClose={() => setShowModal(false)} className={null}>
            <AddTourItemForm />
        </AppModal>

        <AppModal isOpen={showModalTour} onClose={() => setShowModalTour(false)} className={null}>
            <AddTourForm />
        </AppModal>
    </Container>);
}

export default AdminDashboardTours;
